import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { UserCheck, X, Send, MessageCircle, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input, Button } from "@/components/ui-kit";

function formatTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return ""; }
}
function formatDateTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short" }); } catch { return ""; }
}

/**
 * ChatAgentPopout — Item 4b iter v19.
 *
 * Direct chat from klien → all admin/super_admin (non-client agents).
 * Renders for:
 *  - Client account (account_type='klien') — single auto-thread to all admin/super_admin
 *  - Admin/super_admin — list of all 'agent' kind threads (chats from clients)
 */
export default function ChatAgentPopout() {
  const { user } = useAuth();
  const isClient = (user?.account_type || "").toLowerCase() === "klien";
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const enabled = !!user && (isClient || isAdmin);

  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [bootError, setBootError] = useState("");
  const endRef = useRef(null);

  const totalUnread = useMemo(() => threads.reduce((s, t) => s + (t.unread_count || 0), 0), [threads]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      if (isClient) {
        const r = await api.post("/chat/agent/ensure-thread");
        const t = r.data;
        setThreads([t]);
        if (!activeId) setActiveId(t.id);
        setBootError("");
      } else if (isAdmin) {
        const r = await api.get("/chat/agent/threads");
        setThreads(r.data);
      }
    } catch (e) {
      setBootError(e?.response?.data?.detail || "Gagal memuat chat agen");
    }
  }, [enabled, isClient, isAdmin, activeId]);

  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const r = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(r.data);
      await api.post(`/chat/threads/${tid}/read`);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  useEffect(() => { if (open && activeId) loadMessages(activeId); }, [open, activeId, loadMessages]);
  useEffect(() => { if (open && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);
  useEffect(() => {
    if (!open || !activeId) return;
    const id = setInterval(() => loadMessages(activeId), 5000);
    return () => clearInterval(id);
  }, [open, activeId, loadMessages]);

  const grouped = useMemo(() => {
    const out = []; let last = "";
    for (const m of messages) {
      const d = formatDateTs(m.ts);
      if (d !== last) { out.push({ type: "date", key: "d-" + d, date: d }); last = d; }
      out.push({ type: "msg", key: m.id, msg: m });
    }
    return out;
  }, [messages]);

  if (!enabled) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!activeId || !body.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${activeId}/messages`, { body: body.trim() });
      setBody("");
      await loadMessages(activeId);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim pesan");
    } finally { setSending(false); }
  };

  const activeThread = threads.find((t) => t.id === activeId);
  const showList = isAdmin && !activeId;

  // Positioning: client bubble stacks above CS bubble (bottom-right). Admin stacks above CS (bottom-left).
  const bubblePos = isAdmin ? "bottom-[5.5rem] left-5" : "bottom-[5.5rem] right-5";
  const panelPos = isAdmin ? "bottom-5 left-5" : "bottom-5 right-5";

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className={`fixed ${bubblePos} z-40 h-12 w-12 bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-xl rounded-full flex items-center justify-center transition-all hover:scale-105`}
          title={isClient ? "Chat dengan Agen (Admin)" : "Chat Agen — Inbox Klien"}
          data-testid="agent-chat-popout-bubble"
        >
          <UserCheck className="h-5 w-5 pointer-events-none" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold flex items-center justify-center rounded-full font-mono pointer-events-none">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          className={`fixed ${panelPos} z-50 bg-white border border-stone-200 shadow-2xl flex flex-col
                       inset-x-2 top-16 bottom-2 sm:inset-auto sm:w-[380px] sm:max-w-[calc(100vw-1.5rem)] sm:h-[560px] sm:max-h-[calc(100vh-2.5rem)]`}
          data-testid="agent-chat-popout"
        >
          <div className="px-3 py-2.5 border-b border-stone-200 flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50">
            {(isAdmin && activeId) ? (
              <button onClick={() => setActiveId(null)} className="text-stone-500 hover:text-stone-900" title="Kembali" data-testid="agent-chat-back">
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : (
              <UserCheck className="h-4 w-4 text-blue-700" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900 truncate" data-testid="agent-chat-title">
                {isClient ? "Chat Agen (Admin)" : (activeThread ? (activeThread.client_name || activeThread.name) : "Inbox Klien")}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-blue-700">
                {isClient ? "Anda terhubung langsung dengan tim admin" : (showList ? `${threads.length} percakapan${totalUnread > 0 ? ` · ${totalUnread} belum dibaca` : ""}` : "Aktif")}
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-stone-500 hover:text-stone-900" title="Tutup" data-testid="agent-chat-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          {bootError ? (
            <div className="flex-1 flex items-center justify-center p-6 text-center text-xs text-stone-500">{bootError}</div>
          ) : showList ? (
            <div className="flex-1 overflow-y-auto" data-testid="agent-chat-thread-list">
              {threads.length === 0 ? (
                <div className="p-6 text-center text-xs text-stone-400">Belum ada chat dari klien.</div>
              ) : threads.map((t) => {
                const last = t.last_message?.deleted ? "(pesan dihapus)" : (t.last_message?.body || "");
                return (
                  <button key={t.id} onClick={() => setActiveId(t.id)} className="w-full text-left px-3 py-2.5 border-b border-stone-100 hover:bg-blue-50/40 flex items-start gap-2" data-testid={`agent-chat-thread-${t.id}`}>
                    <MessageCircle className="h-3.5 w-3.5 text-blue-700 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="flex-1 truncate text-sm font-medium text-stone-800">{t.client_name || t.name}</span>
                        {t.unread_count > 0 && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-mono">{t.unread_count}</span>}
                      </div>
                      {last && <div className="text-[11px] text-stone-500 truncate mt-0.5">{last}</div>}
                      <div className="text-[10px] text-stone-400 font-mono mt-0.5">{formatDateTs(t.last_message_at)} {formatTs(t.last_message_at)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-2 bg-stone-50/30 space-y-1.5" data-testid="agent-chat-messages">
                {grouped.length === 0 && (
                  <div className="text-center text-xs text-stone-400 py-8">
                    {isClient ? "Halo! Pesan Anda akan langsung diterima oleh tim Admin (bukan AI)." : "Belum ada pesan."}
                  </div>
                )}
                {grouped.map((g) => {
                  if (g.type === "date") {
                    return (<div key={g.key} className="text-center my-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{g.date}</span>
                    </div>);
                  }
                  const m = g.msg;
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={g.key} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-2.5 py-1.5 rounded-lg text-sm ${mine ? "bg-blue-600 text-white" : "bg-white border border-stone-200 text-stone-900"} ${m.deleted ? "italic opacity-50" : ""}`}>
                        {!mine && !m.deleted && <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-0.5">{m.sender_name}</div>}
                        {m.deleted ? <span className="text-xs">(pesan dihapus)</span> : <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                        <div className={`text-[10px] font-mono mt-1 ${mine ? "text-white/70 text-right" : "text-stone-400"}`}>{formatTs(m.ts)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={submit} className="border-t border-stone-200 p-2 bg-white">
                <div className="flex items-end gap-1">
                  <Input value={body} onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
                    placeholder={isClient ? "Tulis pesan untuk admin…" : "Balas pesan klien…"}
                    className="flex-1 !text-sm"
                    data-testid="agent-chat-input"
                  />
                  <Button type="submit" disabled={sending || !body.trim()} className="!px-2 !py-1.5 !bg-blue-600 hover:!bg-blue-700" data-testid="agent-chat-send">
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
